'use client';

import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { useEffect, useRef } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import styles from './ReviewsSlider.module.scss';
import { WhyChooseSliderProps } from '@/types/reviewsSlider.types';

const WhyChooseSlider = ({ slides, title }: WhyChooseSliderProps) => {
  const swiperRef = useRef<SwiperType | null>(null);

  const updateSlideHeights = () => {
    if (!swiperRef.current) return;

    const swiper = swiperRef.current;
    if (!swiper) return;

    // Reset heights
    const slides = swiper.slides;
    slides.forEach((slide: HTMLElement) => {
      slide.style.height = 'auto';
    });

    // Get the maximum height
    let maxHeight = 0;
    slides.forEach((slide: HTMLElement) => {
      const height = slide.offsetHeight;
      maxHeight = Math.max(maxHeight, height);
    });

    // Set all slides to the maximum height
    slides.forEach((slide: HTMLElement) => {
      slide.style.height = `${maxHeight}px`;
    });
  };

  useEffect(() => {
    // Update heights after a short delay to ensure content is rendered
    const timer = setTimeout(updateSlideHeights, 100);

    // Update heights on window resize
    window.addEventListener('resize', updateSlideHeights);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateSlideHeights);
    };
  }, []);

  return (
    <section className="container">
      <div className={styles.sliderWrapper}>
        {title && <h2 className={styles.title}>{title}</h2>}

        <style jsx global>{`
          .swiper-pagination {
            position: absolute;
            bottom: -40px !important;
            left: 0;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .swiper-pagination-bullet {
            width: 19px;
            height: 4px;
            background: #d9d9d9;
            opacity: 1;
            margin: 0 6px;
            cursor: pointer;
            border-radius: 6px;
          }
          .swiper-pagination-bullet-active {
            background: #4d535f;
            width: 48px;
            height: 4px;
          }
        `}</style>
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          modules={[Pagination]}
          pagination={{
            clickable: true,
          }}
          onSlideChange={() => {
            setTimeout(updateSlideHeights, 100);
          }}
          spaceBetween={30}
          slidesPerView={1.3}
          breakpoints={{
            320: {
              slidesPerView: 1,
              spaceBetween: 16,
            },
            576: {
              slidesPerView: 1.1,
              spaceBetween: 16,
            },
            768: {
              slidesPerView: 1.5,
              spaceBetween: 24,
            },
            992: {
              slidesPerView: 2,
              spaceBetween: 30,
            },
            1024: {
              slidesPerView: 2,
              spaceBetween: 40,
            },
          }}
          className={styles.swiper}
        >
          {slides.map((slide) => (
            <SwiperSlide key={slide.id}>
              <div className={styles.slide}>
                <div className={styles.content}>
                  <h3 className={styles.slideTitle}>{slide.slideTitle}</h3>
                  <p
                    className={styles.text}
                    dangerouslySetInnerHTML={{ __html: slide.text }}
                  ></p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default WhyChooseSlider;
